import { useId, useMemo, useState, type ReactNode } from 'react'
import {
  CalendarClock, CheckSquare2, FileText, Mail, NotebookPen, Phone,
} from 'lucide-react'
import { Button } from '../controls/Button'
import { DatePicker } from '../controls/DatePicker'
import { Select, type SelectOption } from '../controls/Select'
import { cx } from '../controls/cx'
import s from './ActivityComposer.module.css'

export type ActivityComposerKind =
  | 'task' | 'appointment' | 'call' | 'email' | 'note' | 'meeting_report'

export interface ActivityComposerContext {
  entity: string
  id: string
  label: string
}

export interface ActivityComposerDraft {
  kind: ActivityComposerKind
  subject: string
  body: string
  about: ActivityComposerContext | null
  direction: 'inbound' | 'outbound' | 'internal'
  dueDate: string
  startDate: string
  startTime: string
  durationMinutes: number
  timezone: string
  location: string
  joinUrl: string
  meetingSource: string
}

export interface ActivityComposerProps {
  initialKind?: ActivityComposerKind
  context?: ActivityComposerContext | null
  /** Host-owned governed person picker. The shared composer owns its placement,
   * not its data access. */
  participants?: ReactNode
  /** Host-owned governed assignee picker; tasks do not flatten assignment into
   * an activity participant. */
  assignee?: ReactNode
  /** Host-governed IANA vocabulary and resolved user/browser default. */
  timezoneOptions?: readonly SelectOption[]
  initialTimezone?: string
  submitting?: boolean
  error?: string | null
  onCancel?: () => void
  onSubmit: (draft: ActivityComposerDraft) => void | Promise<void>
}

const KINDS: Array<{
  key: ActivityComposerKind; label: string; detail: string; icon: ReactNode
}> = [
  { key: 'task', label: 'Task', detail: 'Accountable work', icon: <CheckSquare2 aria-hidden /> },
  { key: 'appointment', label: 'Appointment', detail: 'Scheduled commitment', icon: <CalendarClock aria-hidden /> },
  { key: 'call', label: 'Phone call', detail: 'Conversation', icon: <Phone aria-hidden /> },
  { key: 'email', label: 'Email', detail: 'Message', icon: <Mail aria-hidden /> },
  { key: 'note', label: 'Note', detail: 'Internal record', icon: <NotebookPen aria-hidden /> },
  { key: 'meeting_report', label: 'Meeting report', detail: 'Minutes & outcomes', icon: <FileText aria-hidden /> },
]

const today = () => new Date().toISOString().slice(0, 10)
const localTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

const recognisedTimezone = (timezone: string): boolean => {
  if (!timezone.trim()) return false
  try {
    new Intl.DateTimeFormat('en', { timeZone: timezone }).format()
    return true
  } catch {
    return false
  }
}

/**
 * One creation grammar for Command Marble and the Elevate timeline (#926).
 *
 * This component deliberately has no client, schema or RPC knowledge. The host
 * routes its typed draft to activity/core/engagements, so one visual experience
 * does not become one weak table. Participant and assignee lookup remain slots
 * because each host must search through its own caller-scoped data boundary.
 */
export function ActivityComposer({
  initialKind = 'task', context = null, participants, assignee,
  timezoneOptions = [{ value: 'UTC', label: 'UTC' }], initialTimezone,
  submitting = false, error, onCancel, onSubmit,
}: ActivityComposerProps) {
  const [kind, setKind] = useState<ActivityComposerKind>(initialKind)
  const [about, setAbout] = useState<ActivityComposerContext | null>(context)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [direction, setDirection] = useState<ActivityComposerDraft['direction']>('outbound')
  const [dueDate, setDueDate] = useState('')
  const [startDate, setStartDate] = useState(today())
  const [startTime, setStartTime] = useState('09:00')
  const [durationMinutes, setDurationMinutes] = useState(30)
  const [timezone, setTimezone] = useState(() => {
    const proposed = initialTimezone || localTimezone()
    if (recognisedTimezone(proposed)) return proposed
    return timezoneOptions.find((option) => recognisedTimezone(option.value))?.value ?? 'UTC'
  })
  const [location, setLocation] = useState('')
  const [joinUrl, setJoinUrl] = useState('')
  const [meetingSource, setMeetingSource] = useState('manual')

  const scheduled = kind === 'appointment'
  const accountable = kind === 'task'
  const report = kind === 'meeting_report'
  /* A valid current zone may predate the curated host catalogue. Keep it
     selected and reachable rather than showing a blank trigger. */
  const availableTimezones = useMemo(() => {
    const seen = new Set<string>()
    const candidates = [
      ...(recognisedTimezone(timezone) && timezoneOptions.some((option) => option.value === timezone)
        ? []
        : recognisedTimezone(timezone) ? [{ value: timezone, label: timezone }] : []),
      ...timezoneOptions,
      { value: 'UTC', label: 'UTC' },
    ]
    return candidates.filter((option) => {
      if (!recognisedTimezone(option.value) || seen.has(option.value)) return false
      seen.add(option.value)
      return true
    })
  }, [timezone, timezoneOptions])
  const submitBlockReason = subject.trim().length === 0
    ? `Add a ${report ? 'meeting title' : accountable ? 'task name' : 'subject'} to continue.`
    : scheduled && !startDate
      ? 'Choose an appointment date to continue.'
      : scheduled && !startTime
        ? 'Choose an appointment time to continue.'
        : scheduled && !recognisedTimezone(timezone)
          ? 'Choose a recognised IANA timezone to continue.'
          : null
  const canSubmit = submitBlockReason === null
  const submitReasonId = useId()

  const submitLabel = useMemo(() => ({
    task: 'Create task', appointment: 'Schedule appointment', call: 'Log phone call',
    email: 'Log email', note: 'Save note', meeting_report: 'Create meeting report',
  }[kind]), [kind])

  const submit = () => {
    if (!canSubmit || submitting) return
    void onSubmit({
      kind, subject: subject.trim(), body: body.trim(), about, direction,
      dueDate, startDate, startTime, durationMinutes, timezone: timezone.trim(),
      location: location.trim(), joinUrl: joinUrl.trim(), meetingSource,
    })
  }

  return (
    <div className={s.composer} data-testid="activity-composer">
      <header className={s.header}>
        <div><p className={s.eyebrow}>Do</p><h2>Create activity</h2></div>
        <p>One place to capture work and interaction. Marble keeps each item in its proper system of record.</p>
      </header>

      <div className={s.kinds} role="group" aria-label="Activity type">
        {KINDS.map((item) => (
          <button key={item.key} type="button" aria-pressed={kind === item.key}
            className={cx(s.kind, kind === item.key && s.kindOn)}
            onClick={() => setKind(item.key)} data-testid={`activity-kind-${item.key}`}>
            <span className={s.kindIcon}>{item.icon}</span>
            <span><strong>{item.label}</strong><small>{item.detail}</small></span>
          </button>
        ))}
      </div>

      <div className={s.form}>
        <label className={s.field}>
          <span>{report ? 'Meeting title' : accountable ? 'What needs doing' : 'Subject'}</span>
          <input autoFocus value={subject} onChange={(event) => setSubject(event.target.value)}
            maxLength={200} data-testid="activity-subject" />
        </label>

        <label className={cx(s.field, s.wide)}>
          <span>{report ? 'Notes to start from' : 'Details'} <small>optional</small></span>
          <textarea value={body} onChange={(event) => setBody(event.target.value)} rows={3}
            data-testid="activity-body" />
        </label>

        {about && (
          <div className={cx(s.field, s.wide)}>
            <span>About</span>
            <div className={s.context} data-testid="activity-context">
              <span aria-hidden>◇</span><strong>{about.label}</strong>
              <button type="button" onClick={() => setAbout(null)} aria-label={`Remove ${about.label} context`}>×</button>
            </div>
          </div>
        )}
        {!about && <p className={cx(s.unlinked, s.wide)}>No linked record — this will be created deliberately unlinked.</p>}

        {accountable && (
          <>
            <div className={s.field}><span>Assignee</span>{assignee ?? <p className={s.muted}>Me</p>}</div>
            <div className={s.field}>
              <span>Due <small>optional</small></span>
              <DatePicker value={dueDate} onChange={setDueDate} clearable data-testid="activity-due" />
            </div>
          </>
        )}

        {scheduled && (
          <>
            <div className={s.field}><span>Date</span><DatePicker value={startDate} onChange={setStartDate} required clearable data-testid="activity-date" /></div>
            <label className={s.field}><span>Time</span><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="activity-time" /></label>
            <label className={s.field}><span>Duration</span><select value={durationMinutes} onChange={(e) => setDurationMinutes(Number(e.target.value))}><option value={15}>15 min</option><option value={30}>30 min</option><option value={45}>45 min</option><option value={60}>1 hour</option><option value={90}>90 min</option></select></label>
            <div className={s.field}><span>Timezone</span><Select value={timezone} onValueChange={setTimezone}
              options={[...availableTimezones]} aria-label="Timezone" testId="activity-timezone" /></div>
            <label className={s.field}><span>Location <small>optional</small></span><input value={location} onChange={(e) => setLocation(e.target.value)} /></label>
            <label className={s.field}><span>Video link <small>optional</small></span><input type="url" value={joinUrl} onChange={(e) => setJoinUrl(e.target.value)} /></label>
          </>
        )}

        {(scheduled || report) && participants && (
          <div className={cx(s.field, s.wide)}><span>Participants</span>{participants}</div>
        )}

        {report && (
          <label className={s.field}>
            <span>Meeting origin</span>
            <select value={meetingSource} onChange={(event) => setMeetingSource(event.target.value)}
              data-testid="meeting-source">
              <option value="manual">Manual / in person</option>
              <option value="teams">Microsoft Teams</option>
              <option value="zoom">Zoom</option>
              <option value="webex">Webex</option>
              <option value="conference">Conference</option>
              <option value="other">Other</option>
            </select>
          </label>
        )}

        {!accountable && !report && (
          <label className={s.field}><span>Direction</span><select value={direction} onChange={(e) => setDirection(e.target.value as ActivityComposerDraft['direction'])}><option value="outbound">Outbound</option><option value="inbound">Inbound</option><option value="internal">Internal</option></select></label>
        )}
      </div>

      {error && <p className={s.error} role="alert">{error}</p>}
      <footer className={s.actions}>
        {submitBlockReason && !submitting && (
          <p className={s.muted} id={submitReasonId} role="status">{submitBlockReason}</p>
        )}
        {onCancel && <Button variant="ghost" onClick={onCancel}>Cancel</Button>}
        <Button variant="primary" disabled={!canSubmit || submitting} loading={submitting}
          aria-describedby={submitBlockReason && !submitting ? submitReasonId : undefined}
          onClick={submit} data-testid="activity-submit">{submitLabel}</Button>
      </footer>
    </div>
  )
}
